import {
  FileResponse,
  ScoresGroupsDTO,
  WellInfoClient,
  WellImageDTO,
  SaveImageRequestDTO,
  BooleanPropertyDTO,
  ImageStoreDTO,
  ImageStoreLayoutDTO,
  ScribbleDrawingDTO,
  DeleteScribbleDTO,
  CaptureResultTuningValuesDTO,
  ImageFocusLevelDTO,
  ImagingClient,
  WellHitImageRequestDTO,
  RegionDrawingDTO,
  IWellImagingDetails,
  WellImageParams,
  WellSelectionDTO,
  WellDropImportFlDTO,
  RegionDropLocationDTO,
  WellImageScoreDTO,
  ReportImageRequestDTO,
  IWellImageDTO,
  WellImageDetailsDTO,
  IWellImageDetailsDTO,
  ResponseDTO,
} from 'business/models/Models';
import ModelFactory from 'business/models/ModelFactory';
import BaseService from './BaseService';
import {
  ScoreDefinitionDTO,
  GeneralSetupDTO,
  GeneralSetupClient,
  ScoreClient,
} from '../models/Models';
import { getPlainObject } from 'transformers/utils';
import { transformWellDropImagesMetaData } from 'transformers/well';

export default class WellService extends BaseService {
  async disableImaging(regionIds: number[], imagingEnabled: boolean): Promise<boolean> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true);
    const root: boolean = await wellInfo.disableImaging(regionIds, imagingEnabled);
    return root;
  }

  async getWellImage(
    pid: number,
    wellnumber: number,
    batchId: number,
    dropNumber: number,
    cpID: number,
    regionID: number,
    bestFocusImageRequired = false,
    thumbnailImageType = '',
    forceFetch = true,
  ): Promise<WellImageDTO> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, forceFetch);
    const root: WellImageDTO = await wellInfo.getWellImage(
      pid,
      wellnumber,
      batchId,
      dropNumber,
      cpID,
      regionID,
      0,
      0,
      bestFocusImageRequired,
      thumbnailImageType,
    );
    return root;
  }

  /**
   * @param requestParams - WellImageDetailsDTO containing well and drop details.
   * @returns returns image binary data for the well drop image.
   */
  async getWellDropImageBinary(
    requestParams: IWellImageDetailsDTO,
    forceFetch = true,
  ): Promise<FileResponse> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, forceFetch);
    return await wellInfo.streamWellImage(WellImageDetailsDTO.fromJS(requestParams));
  }

  async getWellIDropImageMetaData(
    experimentId: number,
    batchIds: number[] = [],
    forceFetch = true,
  ): Promise<Record<string, IWellImageDTO>> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, forceFetch);
    const meta: WellImageDTO[] = await wellInfo.getWellImagesMetaData(
      experimentId,
      batchIds,
    );

    return transformWellDropImagesMetaData(meta);
  }

  async getWellImagesBulk(params: WellImageParams[]): Promise<WellImageDTO[]> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false);
    const imagesDataList: WellImageDTO[] = await wellInfo.getWellImages(params);
    return imagesDataList;
  }

  async getWellImageforHitsImageExp(
    wellHitImageRequestDTO: WellHitImageRequestDTO,
  ): Promise<WellImageDTO> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true);
    const root: WellImageDTO = await wellInfo.getWellImageForHitImageExperiments(
      wellHitImageRequestDTO,
    );
    return root;
  }

  async generatePoiCsvFile(
    expId: number,
    dropTab: boolean,
    plateId: number,
    wellNumber: number,
    dropNumber: number,
    regionId?: number,
  ): Promise<FileResponse> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true);
    return await wellInfo.generatePoiCsv(
      expId,
      dropTab,
      plateId || 0,
      wellNumber || 0,
      dropNumber || 0,
      regionId || 0,
    );
  }

  async getWellDrawing(
    pid: number,
    wellNumber: number,
    batchId: number,
    dropNumber: number,
    cpID: number,
    regionName,
  ) {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true);
    return await wellInfo.getWellDrawing(
      pid,
      wellNumber,
      batchId,
      dropNumber,
      cpID,
      regionName,
    );
  }
  async getWellNumbersWithImages(pid: number, dropNumber: number, cpID: number) {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true);
    return await wellInfo.getImagesWellNumbers(pid, dropNumber, cpID);
  }
  async saveGeneralSetupSettings(details: GeneralSetupDTO): Promise<ResponseDTO> {
    const saveGeneralSettingsInfo: GeneralSetupClient = ModelFactory(GeneralSetupClient);
    const root: ResponseDTO = await saveGeneralSettingsInfo.saveGeneralSetupSettings(
      details,
    );
    return root;
  }

  async isWellScoreGroupInUse(details: ScoresGroupsDTO): Promise<number> {
    const wellScoreGroupInfo: ScoreClient = ModelFactory(ScoreClient);
    const root: number = await wellScoreGroupInfo.isWellScoreGroupInUse(details);
    return root;
  }

  async isWellScoreInUse(details: ScoreDefinitionDTO): Promise<number> {
    const wellScoreGroupInfo: ScoreClient = ModelFactory(ScoreClient);
    const root: number = await wellScoreGroupInfo.isWellScoreInUse(details);
    return root;
  }

  async getWellDropScores(): Promise<ScoreDefinitionDTO[]> {
    const wellScoreInfo: ScoreClient = ModelFactory(ScoreClient);
    const root: ScoreDefinitionDTO[] = await wellScoreInfo.getWellDropScores();
    return root;
  }

  async getSendStatisticsOptionStatus(): Promise<BooleanPropertyDTO> {
    const sendStatisticsOptionStatus: GeneralSetupClient =
      ModelFactory(GeneralSetupClient);
    const root: BooleanPropertyDTO =
      await sendStatisticsOptionStatus.getSendStatisticsOptionStatus();
    return root;
  }

  async getImageStoresInfo(): Promise<ImageStoreDTO[]> {
    const client: GeneralSetupClient = ModelFactory(GeneralSetupClient, false, true);
    const root: ImageStoreDTO[] = await client.getImageStoresInfo();
    return root;
  }

  async getImageStoreLayouts(): Promise<ImageStoreLayoutDTO[]> {
    const client: GeneralSetupClient = ModelFactory(GeneralSetupClient);
    const root: ImageStoreLayoutDTO[] = await client.getImageStoreLayouts();
    return root;
  }

  async isImageStoreInUseByImageManagement(
    imageStoreId: number,
  ): Promise<BooleanPropertyDTO> {
    const client: GeneralSetupClient = ModelFactory(GeneralSetupClient);
    const root: BooleanPropertyDTO = await client.isImageStoreInUseByImageManagement(
      imageStoreId,
    );
    return root;
  }

  async isImageStoreInUseByImage(imageStoreId: number): Promise<BooleanPropertyDTO> {
    const client: GeneralSetupClient = ModelFactory(GeneralSetupClient);
    const root: BooleanPropertyDTO = await client.isImageStoreInUseByImage(imageStoreId);
    return root;
  }

  async getSystemFonts(): Promise<string[]> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, false);
    return await wellInfo.getSystemFonts();
  }

  async UpdateDrawing(scribble: ScribbleDrawingDTO) {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true);
    return await wellInfo.updateScribble(scribble);
  }

  async DeleteDrawing(drawing: DeleteScribbleDTO) {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true);
    return await wellInfo.deleteScribble(drawing);
  }

  async getWellImageWithDrawingAnnotations(
    details: ReportImageRequestDTO,
  ): Promise<WellImageDTO> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true);
    return await wellInfo.getWellImageWithDrawingAnnotations(details);
  }

  async getWellImagesWithDrawingsAnnotations(
    details: ReportImageRequestDTO,
  ): Promise<WellImageDTO[]> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true);
    return await wellInfo.getWellImagesWithDrawingAnnotations(details);
  }

  async getWellImagesForImageReport(
    details: SaveImageRequestDTO,
  ): Promise<WellImageDTO[]> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true);
    return await wellInfo.getWellImagesForImageReport(details);
  }

  async savecaptureresulttuningvalues(tuningValues: CaptureResultTuningValuesDTO) {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true);
    return wellInfo.saveCaptureResultTuningValues(tuningValues);
  }

  async getSingleFocusLevelImage(
    pid: number,
    wellnumber: number,
    batchId: number,
    dropNumber: number,
    cpID: number,
    regionName: string | null,
    focusLevelImageId: number | undefined,
  ): Promise<ImageFocusLevelDTO> {
    const imagingClient: ImagingClient = ModelFactory(ImagingClient, false, true);
    const root = imagingClient.getSingleFocusLevel(
      pid,
      wellnumber,
      batchId,
      dropNumber,
      cpID,
      regionName,
      focusLevelImageId,
    );
    return root;
  }

  async getSeedWellImage(
    pid: number,
    wellnumber: number,
    batchId: number,
    dropNumber: number,
    cpID: number,
    regionName: string | null,
    regionID: number,
    bestFocusImageRequired = false,
    thumbnailImageType = '',
  ): Promise<WellImageDTO> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true);
    const root: WellImageDTO = await wellInfo.getSeedWellImage(
      pid,
      wellnumber,
      batchId,
      dropNumber,
      cpID,
      regionName,
      regionID,
      0,
      0,
      bestFocusImageRequired,
      thumbnailImageType,
    );
    return root;
  }

  async saveDrawing(drawing: RegionDrawingDTO, signal?: AbortSignal) {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true, signal);
    return await wellInfo.saveScribble(drawing);
  }

  async addNewScribble(drawing: RegionDrawingDTO, signal?: AbortSignal) {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true, signal);
    return await wellInfo.addNewScribble(drawing);
  }

  async getWellImagingDetails(
    expId: number,
    regionId: number,
    cpId: number,
    imageBatchId: number,
  ): Promise<IWellImagingDetails> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient);
    return getPlainObject(
      await wellInfo.getWellImagingDetails(expId, regionId, cpId, imageBatchId),
    );
  }

  async setWellDropImagingEnabled(
    wells: WellSelectionDTO[],
    imagingEnabled: boolean,
  ): Promise<boolean> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient);
    return await wellInfo.setWellDropImagingEnabled(wells, imagingEnabled);
  }

  async getFrapGraph(
    dropRegionId: number,
    cpId: number,
    imageBatchId: number,
  ): Promise<ImageFocusLevelDTO> {
    const imagingClient: ImagingClient = ModelFactory(ImagingClient, false, true);
    return await imagingClient.getFrapGraph(dropRegionId, cpId, imageBatchId);
  }

  async getFocusLevels(
    plateId: number,
    wellNumber: number,
    imageBatchId: number,
    dropNumber: number,
    cpId: number,
    regionName: string,
  ): Promise<ImageFocusLevelDTO[]> {
    const imagingClient: ImagingClient = ModelFactory(ImagingClient, false, true);
    return await imagingClient.getFocusLevels(
      plateId,
      wellNumber,
      imageBatchId,
      dropNumber,
      cpId,
      regionName,
    );
  }

  async updateWellDropImportFl(wellDrop: WellDropImportFlDTO): Promise<number> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient);
    return await wellInfo.updateWellDropImportFl(wellDrop);
  }

  async saveRegionDropLocation(
    regionDl: RegionDropLocationDTO,
    signal?: AbortSignal,
  ): Promise<number> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true, signal);
    return await wellInfo.saveRegionDropLocation(regionDl);
  }

  async getHighScoreWellImagesWithScoreByContainerNodeId(
    nodeId: number,
    scoreGroupId: number,
    cpID: number,
    signal?: AbortSignal,
  ): Promise<WellImageScoreDTO[]> {
    const wellInfo: WellInfoClient = ModelFactory(WellInfoClient, false, true, signal);
    const imagesDataList: WellImageScoreDTO[] =
      await wellInfo.getHighScoreWellImagesWithScoreByContainerNodeId(
        nodeId,
        scoreGroupId,
        cpID,
      );
    return imagesDataList;
  }

  async getDropFocusLevels(
    plateId: number,
    wellNumber: number,
    imageBatchId: number,
    dropNumber: number,
    cpId: number,
    regionName: string,
  ): Promise<ImageFocusLevelDTO[]> {
    const imagingClient: ImagingClient = ModelFactory(ImagingClient);
    return await imagingClient.getFocusLevels(
      plateId,
      wellNumber,
      imageBatchId,
      dropNumber,
      cpId,
      regionName,
    );
  }
}
